import { RoomApiData } from "../../../types/venue";

export interface VenueLayouts {
  capacity: number;
  id: number;
}

/** Where a venue stands on the public website: "hidden" is published but missing something the website needs. */
export type WebsiteStatus = "live" | "hidden" | "draft" | null;
export type WebsiteFilter = "" | "published" | "not_published" | "live" | "not_live";

export interface Venue {
  id: number;
  name: string;
  address_one: string | null;
  venue_type: string | null;
  completeness_score: number | null;
  website_status?: WebsiteStatus;
  capacity: string | null;
  layouts: VenueLayouts[];
  power_type: string | null;
  internet_service_provider: string | null;
  files: VenueDocument[];
  created_by?: {
    first_name: string;
    last_name: string;
    email: string;
  } | null;

  parent?: { id: number; name: string; venue_type: string } | null;
}

export interface ApiResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: Venue[];
}

export interface VenueDocument {
  id: number;
  file: string;
  file_type: string;
  venue_id?: number;
  venue?: number;
  is_cover?: boolean;
}

export interface VenueBookingService {
  date_of_service: string;
  venue?: { id?: number; name?: string; parent?: { id: number } | null };
}

export interface VenueBooking {
  id: number;
  title?: string;
  service?: string;
  package_item?: { name: string };
  services?: VenueBookingService[];
  events?: Array<{
    start_date_of_event?: string;
    venue?: { id?: number; name?: string; parent?: { id: number } | null };
  }>;

  deceased?: Array<{
    first_name?: string;
    surname?: string;
    person_image?: string;
  }>;
}
export interface VenueContact {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  contact_image: string;
  phone: string;
  twitter_handle: string | null;
  instagram_handle: string | null;
  facebook_handle: string | null;
  created_at: string;
  updated_at: string;
  role: string | null;
  venue: number;
}

export interface VenueLayout {
  id: number;
  layout_type: string;
  capacity: string;
  created_at: string;
  updated_at: string;
  venue: number;
}
export interface VenueChild {
  id: number;
  name: string;
  venue_type: string;
  capacity: string;
  completeness_score: number;
  files: VenueDocument[];
  venue_bookings?: VenueBooking[];
}

export interface VenueData {
  id: number;
  name: string;
  address_one: string;
  address_two: string;

  suburb: string;
  city: string;
  location: string | null;
  capacity: string;
  contact_first_name: string | null;
  contact_last_name: string | null;
  contact_email: string | null;
  contact_phone: string | null;
  time_zone: string;
  completeness_score: number;
  country: string;
  has_power: boolean;
  power_type: string;
  power_outage_rate: string;
  power_socket_type: string;
  power_backup: string;
  backup_power_capacity: string;
  power_distance_from_livestream_desk: string | null;
  internet_service_provider: string | null;
  wifi_name: string;
  wifi_password: string;
  internet_upload_speed: number | null;
  router_accessibility: string;
  router_distance_from_livestream: string | null;
  has_pa_system: boolean;
  pa_system_position: string | null;
  pa_system_distance_from_livestream: string | null;
  pa_system_provider: string | null;
  other_pa_system_service_provider: string | null;
  other_pa_system_providers: string | null;
  pa_system_contact_name: string | null;
  pa_system_contact_phone: string | null;
  pa_system_contact_email: string | null;
  number_of_cameras: string | null;
  camera_positions: string | null;
  distance_of_each_camera_from_livestream_desk: { camera_name: string; cable_length_m: number | string }[] | null;
  floor_plan_image_urls: string | null;
  media_urls: string | null;
  venue_type: string;
  notes: string | null;
  website: string | null;
  facebook: string | null;
  instagram: string | null;
  travel_duration: string | null;
  show_other_input: string | null;
  created_at: string;
  updated_at: string;
  description: string | null;
  amenities: string[];
  layouts: VenueLayout[];
  contacts: VenueContact[];
  files: VenueDocument[];

  children?: VenueChild[];
  rooms?: RoomApiData[];
  venue_bookings?: VenueBooking[];
  created_by?: {
    first_name: string;
    last_name: string;
    email: string;
  } | null;
  last_updated_by?: {
    id: number;
    first_name: string;
    last_name: string;
    email: string;
  } | null;
}

export interface IContact {
  id: string | number;
  first_name?: string;
  last_name?: string;
  email?: string;
  phone?: string;
  role?: string;
  contact_image?: string;
}

export interface IContactData {
  id: number;
  contact_type: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  address_one: string;
  address_two: string;
  suburb: string;
  city: string;
  country: string;
  website: string;
  notes: string;
  role?: string;
  facebook_profile: string;
  instagram_profile: string;
  twitter_profile: string;
  linkedin_profile: string;
  contact_image: string | null;
  contactImageFile: File | null;
}

export interface NewContactForm {
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  role?: string;
}

export interface SimilarVenue {
  id: number;
  name: string;
  address_one: string | null;
  city: string | null;
  completeness_score: number;
  similarity_score: number;
  cover_image: string | null;
  field_scores: Record<string, number>;
}

/** The amenities offered when adding a venue or preparing it for the website. */
export const AMENITY_OPTIONS = [
  "Parking",
  "Wi-Fi",
  "Backup power",
  "PA system",
  "Wheelchair accessibility",
  "Outdoor space",
  "Air conditioning",
  "Projector",
  "Catering",
  "Kitchen",
  "Restrooms",
  "Stage",
  "Security",
  "Accommodation",
];
